import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { CheckCircle2, Info, Loader2, ReceiptText } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import { Modal } from '@/shared/ui/Modal';
import { notificar } from '@/shared/ui/avisos';
import type { PagoAnteriorRegistrado } from './contrato';
import { Campo, ENTRADA, ListaDeTitulos, SeleccionDePersona, type Deudor } from './piezas';

export function ModalDePagoAnterior({
  textoInicial = '',
  alCerrar,
  alRegistrar,
}: {
  textoInicial?: string;
  alCerrar: () => void;
  alRegistrar: () => void;
}) {
  const [busqueda, setBusqueda] = useState(textoInicial);
  const [deudor, setDeudor] = useState<Deudor | null>(null);
  const [desmarcados, setDesmarcados] = useState<Set<number>>(new Set());

  const [factura, setFactura] = useState('');
  const [fecha, setFecha] = useState('');
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [hecho, setHecho] = useState<PagoAnteriorRegistrado | null>(null);

  const hoy = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Guayaquil' });
  const seleccion = deudor?.titulos.filter((t) => !desmarcados.has(t.id)) ?? [];
  const total = seleccion.reduce((suma, t) => suma + t.capital, 0);

  const falta =
    seleccion.length === 0
      ? 'Marque al menos un título'
      : !factura.trim()
        ? 'Falta el número de la factura del sistema anterior'
        : !fecha
          ? 'Falta la fecha en que se pagó'
          : motivo.trim().length < 10
            ? 'Diga con qué se comprobó: quien lo lea después no estuvo aquí'
            : null;

  const registrar = useMutation({
    mutationFn: () =>
      api.post<PagoAnteriorRegistrado>('/titulos/pagos-anteriores', {
        titulos: seleccion.map((t) => t.id),
        numeroDeFactura: factura.trim(),
        fecha,
        motivo: motivo.trim(),
      }),
    onSuccess: (r) => {
      notificar.exito(
        `${r.titulos} ${r.titulos === 1 ? 'título' : 'títulos'} · $${r.capital.toFixed(2)} · factura ${r.numeroDeFactura}`,
        'Registrado como pagado antes',
      );
      setHecho(r);
      alRegistrar();
    },
    onError: (causa) => {
      const mensaje = causa instanceof ErrorDeApi ? causa.message : 'No se pudo registrar';
      setError(mensaje);
      notificar.error(mensaje);
    },
  });

  return (
    <Modal
      abierto
      alCerrar={alCerrar}
      titulo="Ya pagado en el sistema anterior · Art. 8"
      subtitulo="Cierra el título con la factura de antes · no entra en la caja de hoy"
      icono={ReceiptText}
      ancho="ventanilla"
      cerrarAlPulsarFuera={false}
    >
      {hecho ? (
        <div className="rounded-2xl border border-marca-200 bg-marca-50 p-10 text-center">
          <CheckCircle2 className="mx-auto size-10 text-marca-600" aria-hidden />
          <p className="mt-3 text-sm font-bold uppercase text-marca-900">
            {hecho.titulos} {hecho.titulos === 1 ? 'título cerrado' : 'títulos cerrados'}
          </p>
          <p className="mt-1 text-xs uppercase text-marca-800">
            ${hecho.capital.toFixed(2)} · factura {hecho.numeroDeFactura} · {hecho.nombre}
          </p>
          <p className="mt-1 text-[11px] uppercase text-marca-700">
            No se emitió factura ni se registró cobro: su cierre de caja no cambia
          </p>
          <button
            type="button"
            onClick={alCerrar}
            className="mt-5 inline-flex h-10 items-center rounded-lg border border-armazon-200 bg-white px-4 text-xs font-semibold uppercase text-armazon-700 hover:bg-armazon-50"
          >
            Cerrar
          </button>
        </div>
      ) : (
        <>
          <SeleccionDePersona
            pregunta="De quién son los títulos"
            busqueda={busqueda}
            alBuscar={setBusqueda}
            deudor={deudor}
            alElegir={(d) => {
              setDeudor(d);
              setDesmarcados(new Set());
              setError(null);
            }}
            alLimpiar={() => {
              setDeudor(null);
              setBusqueda('');
            }}
            textoInicial={textoInicial}
          />

          {deudor === null ? (
            <p className="rounded-2xl border border-armazon-200 bg-white p-12 text-center text-sm uppercase text-armazon-400">
              Busque a la persona que trae su factura de antes
            </p>
          ) : (
            <div className="grid gap-5 lg:grid-cols-[1fr_22rem]">
              <div className="space-y-4">
                <ListaDeTitulos
                  titulos={deudor.titulos}
                  desmarcados={desmarcados}
                  alCambiar={setDesmarcados}
                />
                <p className="flex gap-3 rounded-xl border border-armazon-200 bg-armazon-50 p-4 text-justify text-[11px] uppercase leading-relaxed text-armazon-700">
                  <Info className="mt-0.5 size-4 shrink-0" aria-hidden />
                  <span>
                    <span className="font-semibold">Esto no cobra nada.</span> El dinero entró en
                    el sistema anterior y allí está contado: aquí no se emite factura ni se
                    registra pago, y el cierre de caja del día no cambia. Queda anotado quién lo
                    registró, y se puede deshacer.
                  </span>
                </p>
              </div>

              <div className="space-y-4 rounded-2xl border border-armazon-200 bg-white p-5">
                <Campo etiqueta="N.º de la factura anterior">
                  <input
                    value={factura}
                    onChange={(e) => setFactura(e.target.value)}
                    maxLength={60}
                    placeholder="001-002-000009001"
                    className={`${ENTRADA} uppercase`}
                  />
                </Campo>

                <Campo etiqueta="Fecha en que se pagó">
                  <input
                    type="date"
                    value={fecha}
                    max={hoy}
                    onChange={(e) => setFecha(e.target.value)}
                    className={ENTRADA}
                  />
                </Campo>

                <Campo etiqueta="Con qué se comprobó">
                  <textarea
                    value={motivo}
                    onChange={(e) => setMotivo(e.target.value)}
                    maxLength={300}
                    rows={3}
                    placeholder="Presentó la factura original sellada por caja…"
                    className={`${ENTRADA} h-auto py-2`}
                  />
                </Campo>

                <div className="rounded-xl bg-armazon-900 p-4 text-white">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-white/60">
                    Se cierran
                  </p>
                  <p className="importe mt-1 text-2xl font-bold">${total.toFixed(2)}</p>
                  <p className="text-[11px] uppercase text-white/60">
                    {seleccion.length} {seleccion.length === 1 ? 'título' : 'títulos'} · sin
                    factura ni caja
                  </p>
                </div>

                {(error ?? falta) && (
                  <p
                    role={error ? 'alert' : undefined}
                    className={`text-[11px] font-semibold uppercase ${error ? 'text-red-700' : 'text-armazon-500'}`}
                  >
                    {error ?? falta}
                  </p>
                )}

                <button
                  type="button"
                  disabled={falta !== null || registrar.isPending}
                  onClick={() => {
                    setError(null);
                    registrar.mutate();
                  }}
                  className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-armazon-800 text-xs font-bold uppercase text-white transition-colors hover:bg-armazon-900 disabled:opacity-40"
                >
                  {registrar.isPending ? (
                    <Loader2 className="size-4 animate-spin" aria-hidden />
                  ) : (
                    <ReceiptText className="size-4" aria-hidden />
                  )}
                  Registrar como ya pagado
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </Modal>
  );
}
